import type {
  ProactivityExperimentArtifactState,
  ProactivityExperimentDeliveryKind,
  ProactivityExperimentRecipient,
} from './proactivity-experiments-types';

const failureMessages: Record<string, string> = {
  card_create_failed: 'Card creation failed',
  experiment_revoked_before_issue: 'Experiment was revoked before issue',
};

type RecipientGroupSpec = {
  label: string;
  includes: (recipient: ProactivityExperimentRecipient) => boolean;
};

// The artifact state only means something once the artifact was issued
const byState = (
  label: string,
  state: ProactivityExperimentArtifactState
): RecipientGroupSpec => ({
  label,
  includes: (recipient) =>
    recipient.issuance_status === 'issued' &&
    recipient.artifact_state === state,
});

const issuanceGroups: RecipientGroupSpec[] = [
  {
    label: 'Failed to issue',
    includes: (recipient) => recipient.issuance_status === 'failed',
  },
  {
    label: 'Pending issuance',
    includes: (recipient) => recipient.issuance_status === 'pending',
  },
];

// Active rows grouped by what happened to the artifact, in the order the states usually progress
const groupsByKind: Record<
  ProactivityExperimentDeliveryKind,
  RecipientGroupSpec[]
> = {
  card_prompt: [
    byState("Haven't touched it", 'untouched'),
    byState('Opened CTA', 'opened'),
    byState('Rejected', 'rejected'),
    byState('Revoked', 'revoked'),
    ...issuanceGroups,
    byState('Data issue', 'unknown'),
  ],
  direct_routine: [
    byState('Routine active', 'active'),
    byState('Routine paused', 'paused'),
    byState('Routine retired', 'retired'),
    byState('Routine deleted by user', 'deleted'),
    ...issuanceGroups,
  ],
  direct_chat: [
    byState('Chat created', 'created'),
    byState('Chat deleted by user', 'deleted'),
    ...issuanceGroups,
  ],
};

const RecipientGroup = ({
  label,
  members,
}: {
  label: string;
  members: ProactivityExperimentRecipient[];
}) => {
  if (members.length === 0) return null;

  return (
    <section className="space-y-2">
      <h4 className="text-sm font-medium">
        {label} ({members.length})
      </h4>
      <ul className="divide-y rounded-md border bg-background">
        {members.map((recipient) => (
          <li
            key={recipient.id}
            className="flex flex-col gap-1 px-3 py-2 text-sm sm:flex-row sm:flex-wrap sm:items-center sm:justify-between"
          >
            <span className="break-words">
              {recipient.name || 'Unnamed user'}
            </span>
            <span className="break-all text-muted-foreground">
              {recipient.email}
              {recipient.source === 'signup' ? (
                <span className="ml-2 rounded border px-1 text-xs">Signup</span>
              ) : null}
            </span>
            {recipient.removed_at ? (
              <span className="w-full text-xs text-muted-foreground">
                Removed {new Date(recipient.removed_at).toLocaleString()} ·{' '}
                {recipient.artifact_state}
              </span>
            ) : null}
            {recipient.issuance_status === 'failed' ? (
              <span className="w-full text-xs text-destructive">
                {failureMessages[recipient.failure_code ?? ''] ??
                  'Could not issue card'}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
};

type ProactivityExperimentRecipientListsProps = {
  deliveryKind: ProactivityExperimentDeliveryKind;
  recipients: ProactivityExperimentRecipient[];
};

// Rows, not people: a user dropped and re-added has one removed row and one active row
export const ProactivityExperimentRecipientLists = ({
  deliveryKind,
  recipients,
}: ProactivityExperimentRecipientListsProps) => {
  const active = recipients.filter(
    (recipient) => recipient.removed_at === null
  );
  const removed = recipients
    .filter((recipient) => recipient.removed_at !== null)
    .sort((a, b) => (a.removed_at! < b.removed_at! ? 1 : -1));

  return (
    <div className="space-y-4">
      {groupsByKind[deliveryKind].map((group) => (
        <RecipientGroup
          key={group.label}
          label={group.label}
          members={active.filter(group.includes)}
        />
      ))}
      <RecipientGroup label="Removed from cohort" members={removed} />
    </div>
  );
};
